import { Component, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionConfig, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Phone, CalendarCheck, MessageCircle, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Services from "@/components/Services";
import WhyUs from "@/components/WhyUs";
import Doctors from "@/components/Doctors";
import Results from "@/components/Results";
import Gallery from "@/components/Gallery";
import Appointment from "@/components/Appointment";
import Location from "@/components/Location";
import Footer from "@/components/Footer";
import Banner from "@/components/Banner";
import AdminPage from "@/pages/Admin";
import { scrollToId } from "@/components/Reveal";
import { LangProvider, useLang } from "@/i18n";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6 text-center">
                    <div>
                        <p className="font-display text-3xl font-semibold text-brand-navy">Something went wrong.</p>
                        <p className="mt-2 text-sm text-slate-500">Please refresh the page.</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function MobileCallBar() {
    const { t } = useLang();
    return (
        <div
            data-testid="mobile-call-bar"
            className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-3 border-t border-slate-200 bg-white/90 p-3 backdrop-blur-xl lg:hidden"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
            <a
                data-testid="mobile-call-btn"
                href="tel:+917208991091"
                className="flex items-center justify-center gap-2 rounded-full border border-slate-300 py-3 text-sm font-semibold text-brand-navy"
            >
                <Phone className="h-4 w-4" />
                {t.mobile.call}
            </a>
            <button
                data-testid="mobile-book-btn"
                onClick={() => scrollToId("appointment")}
                className="flex items-center justify-center gap-2 rounded-full bg-brand-navy py-3 text-sm font-semibold text-white"
            >
                <CalendarCheck className="h-4 w-4" />
                {t.mobile.book}
            </button>
        </div>
    );
}

function WhatsAppIcon({ className = "" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
    );
}

function FloatingWhatsApp() {
    const { t } = useLang();
    const [open, setOpen] = useState(false);

    const waLink = (service) =>
        `https://wa.me/917208991091?text=${encodeURIComponent(
            service ? `${t.wa.msgPrefix} ${service}` : t.wa.msgPrefix
        )}`;

    return (
        <div className="fixed bottom-24 right-4 z-40 lg:bottom-6 lg:right-6">
            {open && <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />}
            <AnimatePresence>
                {open && (
                    <div
                        data-testid="whatsapp-quick-menu"
                        className="absolute bottom-[4.5rem] right-0 z-50 w-64 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lift"
                    >
                        <div className="flex items-center justify-between bg-brand-navy px-5 py-4">
                            <p className="text-sm font-bold text-white">{t.wa.title}</p>
                            <button
                                data-testid="whatsapp-menu-close"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                                className="rounded-full p-1 text-white/70 transition-colors hover:text-white"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="max-h-72 overflow-y-auto p-2">
                            {t.appt.services.map((s) => (
                                <a
                                    key={s}
                                    data-testid={`whatsapp-quick-reply-${s}`}
                                    href={waLink(s)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded-xl px-4 py-2.5 text-sm font-medium text-brand-navy transition-colors duration-200 hover:bg-brand-olivelight"
                                >
                                    {s}
                                </a>
                            ))}
                        </div>
                        <a
                            data-testid="whatsapp-general-enquiry"
                            href={waLink(null)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 border-t border-slate-100 bg-brand-olivelight/60 px-4 py-3 text-xs font-bold text-brand-olive"
                        >
                            <MessageCircle className="h-3.5 w-3.5" />
                            {t.wa.general}
                        </a>
                    </div>
                )}
            </AnimatePresence>
            <button
                data-testid="whatsapp-float-button"
                onClick={() => setOpen((o) => !o)}
                aria-label={t.wa.title}
                className="group relative z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_12px_30px_-8px_rgba(37,211,102,0.65)] transition-all duration-300 hover:scale-110 hover:shadow-[0_18px_40px_-8px_rgba(37,211,102,0.8)]"
            >
                <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-20" />
                {open ? <X className="relative h-7 w-7" /> : <WhatsAppIcon className="relative h-7 w-7" />}
                <span className="pointer-events-none absolute right-full mr-3 hidden whitespace-nowrap rounded-full bg-brand-navy px-4 py-2 text-xs font-semibold text-white opacity-0 shadow-lift transition-all duration-300 group-hover:opacity-100 lg:block">
                    {t.wa.title}
                </span>
            </button>
        </div>
    );
}

function SiteShell() {
    const { isMr } = useLang();
    return (
        <div className={`relative ${isMr ? "lang-mr" : ""}`}>
            <div className="grain-overlay" />
            <Navbar />
            <main className="pb-20 lg:pb-0">
                <Banner />
                <Hero />
                <Marquee />
                <Services />
                <WhyUs />
                <Doctors />
                <Results />
                <Gallery />
                <Appointment />
                <Location />
            </main>
            <Footer />
            <MobileCallBar />
            <FloatingWhatsApp />
        </div>
    );
}

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <ErrorBoundary>
            <LangProvider>
                <MotionConfig reducedMotion="user">
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<SiteShell />} />
                            <Route path="/admin" element={<AdminPage />} />
                        </Routes>
                    </BrowserRouter>
                </MotionConfig>
            </LangProvider>
        </ErrorBoundary>
    );
}

export default App;
